/**
 * 工作流画布状态管理 Store
 *
 * 使用 Zustand + immer 中间件管理工作流画布状态
 * 参考: docs/ragflow_web 工作流设计
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
  type NodeChange,
  type EdgeChange,
  type Connection,
} from '@xyflow/react';
import type {
  WorkflowNode,
  WorkflowEdge,
  WorkflowNodeData,
  WorkflowNodeType,
  NodeExecutionLog,
} from '../types/workflow';
import { createDefaultNodeData } from '../types/workflow';

// ==================== View Mode ====================

/** 工作流视图模式 */
export type WorkflowViewMode = 'list' | 'editor';

// ==================== WorkflowStore ====================

/** 节点验证错误 */
export interface NodeValidationError {
  nodeId: string;
  field: string;
  message: string;
}

interface WorkflowState {
  // 视图模式
  viewMode: WorkflowViewMode;
  // 节点和边
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];

  // 选中的节点 ID
  selectedNodeId: string | null;

  // 当前工作流信息
  workflowId: string | null;
  workflowName: string;

  // 是否有未保存的更改
  isDirty: boolean;

  // 节点验证错误
  validationErrors: Map<string, NodeValidationError[]>;

  // 操作方法
  onNodesChange: OnNodesChange<WorkflowNode>;
  onEdgesChange: OnEdgesChange<WorkflowEdge>;
  onConnect: OnConnect;

  // 节点操作
  addNode: (type: WorkflowNodeType, position: { x: number; y: number }) => string;
  updateNodeForm: (nodeId: string, values: Partial<WorkflowNodeData>) => void;
  duplicateNode: (nodeId: string) => string | null;
  deleteNodeById: (nodeId: string) => void;
  setNodePosition: (nodeId: string, position: { x: number; y: number }) => void;

  // 边操作
  deleteEdgeById: (edgeId: string) => void;

  // 选择操作
  setSelectedNodeId: (nodeId: string | null) => void;

  // 视图模式操作
  setViewMode: (mode: WorkflowViewMode) => void;
  showList: () => void;
  showEditor: (workflowId?: string) => void;

  // 工作流操作
  setWorkflow: (workflowId: string, name: string, nodes: WorkflowNode[], edges: WorkflowEdge[]) => void;
  clearWorkflow: () => void;
  markClean: () => void;

  // 验证操作
  setNodeValidationErrors: (nodeId: string, errors: NodeValidationError[]) => void;
  clearNodeValidationErrors: (nodeId: string) => void;
  clearAllValidationErrors: () => void;
  getNodeValidationErrors: (nodeId: string) => NodeValidationError[];
  hasValidationErrors: () => boolean;

  // 获取方法
  getNodeById: (nodeId: string) => WorkflowNode | undefined;
  getNodesByType: (type: WorkflowNodeType) => WorkflowNode[];
  
  // 循环容器操作（Subflow/Group）
  addNodeToLoop: (nodeId: string, loopId: string) => void;
  removeNodeFromLoop: (nodeId: string) => void;
  getLoopChildren: (loopId: string) => WorkflowNode[];
  createLoopWithNodes: (nodeIds: string[], position: { x: number; y: number }) => string;
  findContainingLoop: (position: { x: number; y: number }, excludeNodeId?: string) => WorkflowNode | null;
  updateNodeParent: (nodeId: string, newPosition: { x: number; y: number }) => void;
}

export const useWorkflowStore = create<WorkflowState>()(
  immer((set, get) => ({
    // 初始状态
    viewMode: 'list',
    nodes: [],
    edges: [],
    selectedNodeId: null,
    workflowId: null,
    workflowName: '未命名工作流',
    isDirty: false,
    validationErrors: new Map<string, NodeValidationError[]>(),

    // 节点变化处理
    onNodesChange: (changes: NodeChange<WorkflowNode>[]) => {
      set((state) => {
        state.nodes = applyNodeChanges(changes, state.nodes) as WorkflowNode[];
        // 标记为有更改
        if (changes.some(c => c.type !== 'select' && c.type !== 'position')) {
          state.isDirty = true;
        }
      });
    },

    // 边变化处理
    onEdgesChange: (changes: EdgeChange<WorkflowEdge>[]) => {
      set((state) => {
        state.edges = applyEdgeChanges(changes, state.edges) as WorkflowEdge[];
        // 标记为有更改
        if (changes.some(c => c.type !== 'select')) {
          state.isDirty = true;
        }
      });
    },

    // 连接处理
    onConnect: (connection: Connection) => {
      set((state) => {
        const newEdge = addEdge(
          {
            ...connection,
            id: `edge-${Date.now()}`,
            type: 'default',
          },
          state.edges
        ) as WorkflowEdge[];
        state.edges = newEdge;
        state.isDirty = true;
      });
    },

    // 添加节点
    addNode: (type: WorkflowNodeType, position: { x: number; y: number }) => {
      // 不允许在循环容器内嵌套循环容器
      if (type === 'loop') {
        const containingLoop = get().findContainingLoop(position);
        if (containingLoop) {
          console.warn('不能在循环容器内创建循环容器');
          return null as unknown as string;
        }
      }

      const nodeId = `${type}-${Date.now()}`;
      const defaultData = createDefaultNodeData(type);

      // 检测是否落在循环容器内
      const containingLoop = get().findContainingLoop(position);

      set((state) => {
        const newNode: WorkflowNode = {
          id: nodeId,
          type,
          position,
          data: defaultData,
        };

        // loop 节点需要设置默认尺寸，否则 subflows 无法工作
        if (type === 'loop') {
          newNode.style = {
            width: 400,
            height: 300,
          };
        }

        // 如果落在循环容器内，设置父子关系
        if (containingLoop) {
          newNode.parentId = containingLoop.id;
          newNode.extent = 'parent';

          // 计算相对于循环容器的位置
          newNode.position = {
            x: position.x - containingLoop.position.x,
            y: position.y - containingLoop.position.y,
          };

          // 更新循环容器的 children 列表
          const loopData = containingLoop.data as Record<string, unknown>;
          if (!loopData.children) {
            loopData.children = [nodeId];
          } else if (Array.isArray(loopData.children)) {
            if (!loopData.children.includes(nodeId)) {
              (loopData.children as string[]).push(nodeId);
            }
          }
        }

        state.nodes.push(newNode);
        state.isDirty = true;

        // 确保父节点在子节点之前（ReactFlow 要求）
        if (newNode.parentId) {
          state.nodes.sort((a, b) => {
            // 如果 a 是 b 的父节点，a 排在前面
            if (a.id === b.parentId) return -1;
            // 如果 b 是 a 的父节点，b 排在前面
            if (b.id === a.parentId) return 1;
            return 0;
          });
        }
      });

      return nodeId;
    },

    // 更新节点配置
    updateNodeForm: (nodeId: string, values: Partial<WorkflowNodeData>) => {
      set((state) => {
        const node = state.nodes.find((n) => n.id === nodeId);
        if (node) {
          Object.assign(node.data, values);
          state.isDirty = true;
        }
      });
    },

    // 复制节点
    duplicateNode: (nodeId: string) => {
      const node = get().nodes.find((n) => n.id === nodeId);
      if (!node) return null;

      const newNodeId = `${node.type}-${Date.now()}`;
      set((state) => {
        state.nodes.push({
          ...node,
          id: newNodeId,
          position: {
            x: node.position.x + 20,
            y: node.position.y + 20,
          },
          data: {
            ...node.data,
            label: `${node.data.label} (副本)`,
          },
          selected: false,
        });
        state.isDirty = true;
      });

      return newNodeId;
    },

    // 删除节点
    deleteNodeById: (nodeId: string) => {
      set((state) => {
        // 删除节点
        state.nodes = state.nodes.filter((n) => n.id !== nodeId);
        // 删除相关的边
        state.edges = state.edges.filter(
          (e) => e.source !== nodeId && e.target !== nodeId
        );
        // 清除选中状态
        if (state.selectedNodeId === nodeId) {
          state.selectedNodeId = null;
        }
        state.isDirty = true;
      });
    },

    // 设置节点位置
    setNodePosition: (nodeId: string, position: { x: number; y: number }) => {
      set((state) => {
        const node = state.nodes.find((n) => n.id === nodeId);
        if (node) {
          node.position = position;
          state.isDirty = true;
        }
      });
    },

    // 删除边
    deleteEdgeById: (edgeId: string) => {
      set((state) => {
        state.edges = state.edges.filter((e) => e.id !== edgeId);
        state.isDirty = true;
      });
    },

    // 设置选中节点
    setSelectedNodeId: (nodeId: string | null) => {
      set((state) => {
        state.selectedNodeId = nodeId;
      });
    },

    // 设置视图模式
    setViewMode: (mode: WorkflowViewMode) => {
      set((state) => {
        state.viewMode = mode;
      });
    },

    // 显示列表视图
    showList: () => {
      set((state) => {
        state.viewMode = 'list';
      });
    },

    // 显示编辑器视图
    showEditor: (workflowId?: string) => {
      set((state) => {
        state.viewMode = 'editor';
        if (workflowId) {
          state.workflowId = workflowId;
        }
      });
    },

    // 设置工作流
    setWorkflow: (
      workflowId: string,
      name: string,
      nodes: WorkflowNode[],
      edges: WorkflowEdge[]
    ) => {
      set((state) => {
        state.workflowId = workflowId;
        state.workflowName = name;
        state.nodes = nodes;
        state.edges = edges;
        state.selectedNodeId = null;
        state.isDirty = false;
      });
    },

    // 清除工作流
    clearWorkflow: () => {
      set((state) => {
        state.nodes = [];
        state.edges = [];
        state.selectedNodeId = null;
        state.workflowId = null;
        state.workflowName = '未命名工作流';
        state.isDirty = false;
      });
    },

    // 标记为已保存
    markClean: () => {
      set((state) => {
        state.isDirty = false;
      });
    },

    // 获取节点
    getNodeById: (nodeId: string) => {
      return get().nodes.find((n) => n.id === nodeId);
    },

    // 按类型获取节点
    getNodesByType: (type: WorkflowNodeType) => {
      return get().nodes.filter((n) => n.type === type);
    },

    // 设置节点验证错误
    setNodeValidationErrors: (nodeId: string, errors: NodeValidationError[]) => {
      set((state) => {
        if (errors.length === 0) {
          state.validationErrors.delete(nodeId);
        } else {
          state.validationErrors.set(nodeId, errors);
        }
      });
    },

    // 清除节点验证错误
    clearNodeValidationErrors: (nodeId: string) => {
      set((state) => {
        state.validationErrors.delete(nodeId);
      });
    },

    // 清除所有验证错误
    clearAllValidationErrors: () => {
      set((state) => {
        state.validationErrors = new Map();
      });
    },

    // 获取节点验证错误
    getNodeValidationErrors: (nodeId: string) => {
      return get().validationErrors.get(nodeId) || [];
    },

    // 检查是否有验证错误
    hasValidationErrors: () => {
      return get().validationErrors.size > 0;
    },

    // ==================== 循环容器操作（Subflow/Group） ====================

    /**
     * 将节点添加到循环容器中
     * 设置节点的 parentId 和 extent: 'parent'
     */
    addNodeToLoop: (nodeId: string, loopId: string) => {
      set((state) => {
        const node = state.nodes.find((n) => n.id === nodeId);
        const loopNode = state.nodes.find((n) => n.id === loopId);
        
        if (node && loopNode && loopNode.type === 'loop') {
          // 计算相对于循环容器的位置
          const loopPosition = loopNode.position;
          const nodePosition = node.position;
          
          // 设置父节点和限制范围
          node.parentId = loopId;
          node.extent = 'parent';
          
          // 计算相对位置（相对于循环容器内部）
          node.position = {
            x: nodePosition.x - loopPosition.x,
            y: nodePosition.y - loopPosition.y,
          };
          
          // 更新循环容器的子节点列表
          const loopData = loopNode.data as Record<string, unknown>;
          if (!loopData.children) {
            loopData.children = [nodeId];
          } else if (!Array.isArray(loopData.children)) {
            loopData.children = [nodeId];
          } else if (!(loopData.children as string[]).includes(nodeId)) {
            (loopData.children as string[]).push(nodeId);
          }
          
          state.isDirty = true;
        }
      });
    },

    /**
     * 从循环容器中移除节点
     * 清除节点的 parentId 和 extent
     */
    removeNodeFromLoop: (nodeId: string) => {
      set((state) => {
        const node = state.nodes.find((n) => n.id === nodeId);
        
        if (node && node.parentId) {
          const loopId = node.parentId;
          const loopNode = state.nodes.find((n) => n.id === loopId);
          
          // 计算绝对位置
          if (loopNode) {
            const loopPosition = loopNode.position;
            node.position = {
              x: node.position.x + loopPosition.x,
              y: node.position.y + loopPosition.y,
            };
            
            // 从循环容器的子节点列表中移除
            const loopData = loopNode.data as Record<string, unknown>;
            if (loopData.children && Array.isArray(loopData.children)) {
              loopData.children = (loopData.children as string[]).filter(
                (id) => id !== nodeId
              );
            }
          }
          
          // 清除父节点引用
          delete node.parentId;
          delete node.extent;
          
          state.isDirty = true;
        }
      });
    },

    /**
     * 获取循环容器的所有子节点
     */
    getLoopChildren: (loopId: string) => {
      return get().nodes.filter((n) => n.parentId === loopId);
    },

    /**
     * 创建循环容器并包含指定的节点
     */
    createLoopWithNodes: (nodeIds: string[], position: { x: number; y: number }) => {
      const loopId = `loop-${Date.now()}`;
      const defaultData = createDefaultNodeData('loop');
      
      // 计算包含所有节点的边界框
      const nodes = get().nodes.filter((n) => nodeIds.includes(n.id));
      if (nodes.length === 0) {
        return loopId;
      }
      
      // 找到最小和最大坐标
      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;
      
      nodes.forEach((node) => {
        minX = Math.min(minX, node.position.x);
        minY = Math.min(minY, node.position.y);
        maxX = Math.max(maxX, node.position.x + 200); // 假设节点宽度为 200
        maxY = Math.max(maxY, node.position.y + 100); // 假设节点高度为 100
      });
      
      // 添加内边距
      const padding = 40;
      const loopWidth = maxX - minX + padding * 2;
      const loopHeight = maxY - minY + padding * 2;
      
      set((state) => {
        // 创建循环容器节点
        const loopNode: WorkflowNode = {
          id: loopId,
          type: 'loop',
          position: {
            x: position.x,
            y: position.y,
          },
          style: {
            width: loopWidth,
            height: loopHeight,
          },
          data: {
            ...defaultData,
            children: nodeIds,
          },
        };
        
        state.nodes.push(loopNode);
        
        // 更新子节点
        nodes.forEach((node) => {
          node.parentId = loopId;
          node.extent = 'parent';
          // 调整位置为相对于循环容器的位置
          node.position = {
            x: node.position.x - minX + padding,
            y: node.position.y - minY + padding,
          };
        });
        
        state.isDirty = true;
      });
      
      return loopId;
    },

    /**
     * 查找包含指定位置的循环容器
     * @param position 节点位置
     * @param excludeNodeId 排除的节点 ID（用于避免把自己当作父节点）
     * @returns 包含该位置的循环容器节点，如果没有则返回 null
     */
    findContainingLoop: (position: { x: number; y: number }, excludeNodeId?: string) => {
      const nodes = get().nodes;

      // 查找所有循环容器节点
      const loopNodes = nodes.filter(
        (n) => n.type === 'loop' && n.id !== excludeNodeId
      );

      // 检查位置是否在某个循环容器内
      for (const loopNode of loopNodes) {
        const style = loopNode.style;
        // 为没有 style 的循环容器使用默认尺寸
        const width = style && typeof style.width === 'number' ? style.width :
                      style && typeof style.width === 'string' ? parseFloat(style.width) : 400;
        const height = style && typeof style.height === 'number' ? style.height :
                       style && typeof style.height === 'string' ? parseFloat(style.height) : 300;

        const loopBounds = {
          left: loopNode.position.x,
          top: loopNode.position.y,
          right: loopNode.position.x + width,
          bottom: loopNode.position.y + height,
        };

        // 添加一些边距，使检测更宽松
        const margin = 10;
        if (
          position.x >= loopBounds.left + margin &&
          position.x <= loopBounds.right - margin &&
          position.y >= loopBounds.top + margin &&
          position.y <= loopBounds.bottom - margin
        ) {
          return loopNode;
        }
      }

      return null;
    },

    /**
     * 更新节点的父节点关系（用于拖拽移动节点时）
     * @param nodeId 节点 ID
     * @param newPosition 新的位置（可能是相对位置）
     */
    updateNodeParent: (nodeId: string, newPosition: { x: number; y: number }) => {
      console.log('[updateNodeParent] 开始:', nodeId, 'newPosition:', newPosition);

      set((state) => {
        const node = state.nodes.find((n) => n.id === nodeId);
        if (!node) {
          console.log('[updateNodeParent] 节点不存在:', nodeId);
          return;
        }

        // 计算节点的绝对位置
        let absolutePosition = { ...newPosition };
        if (node.parentId) {
          const parent = state.nodes.find((n) => n.id === node.parentId);
          if (parent) {
            absolutePosition = {
              x: newPosition.x + parent.position.x,
              y: newPosition.y + parent.position.y,
            };
            console.log('[updateNodeParent] 节点已有父节点, 绝对位置:', absolutePosition);
          }
        }

        // 节点中心点（绝对位置）
        const nodeCenter = {
          x: absolutePosition.x + 100,
          y: absolutePosition.y + 50,
        };

        // 查找所有循环容器
        const loopNodes = state.nodes.filter((n) => n.type === 'loop' && n.id !== nodeId);
        console.log('[updateNodeParent] 循环容器数量:', loopNodes.length);

        // 查找包含该位置的循环容器
        const containingLoop = state.nodes.find((n) => {
          if (n.type !== 'loop' || n.id === nodeId) return false;

          const style = n.style;
          // 为没有 style 的循环容器使用默认尺寸
          const width = style && typeof style.width === 'number' ? style.width :
                        style && typeof style.width === 'string' ? parseFloat(style.width) : 400;
          const height = style && typeof style.height === 'number' ? style.height :
                         style && typeof style.height === 'string' ? parseFloat(style.height) : 300;

          const loopBounds = {
            left: n.position.x,
            top: n.position.y,
            right: n.position.x + width,
            bottom: n.position.y + height,
          };

          const isInLoop = (
            nodeCenter.x >= loopBounds.left &&
            nodeCenter.x <= loopBounds.right &&
            nodeCenter.y >= loopBounds.top &&
            nodeCenter.y <= loopBounds.bottom
          );

          if (isInLoop) {
            console.log('[updateNodeParent] 节点在循环容器内:', n.id, 'bounds:', loopBounds, 'center:', nodeCenter);
          }

          return isInLoop;
        });

        const currentParentId = node.parentId;
        const newParentId = containingLoop?.id;

        console.log('[updateNodeParent] currentParentId:', currentParentId, 'newParentId:', newParentId);

        // 辅助函数：确保父节点在子节点之前
        const ensureParentBeforeChild = () => {
          state.nodes.sort((a, b) => {
            if (a.id === b.parentId) return -1;
            if (b.id === a.parentId) return 1;
            return 0;
          });
        };

        // 情况 1：从无父节点 -> 有父节点（拖入循环容器）
        if (!currentParentId && newParentId) {
          console.log('[updateNodeParent] 拖入循环容器');
          node.parentId = newParentId;
          node.extent = 'parent';

          // 调整位置为相对位置
          node.position = {
            x: absolutePosition.x - containingLoop!.position.x,
            y: absolutePosition.y - containingLoop!.position.y,
          };

          // 更新循环容器的 children
          const loopData = containingLoop!.data as Record<string, unknown>;
          if (!loopData.children) {
            loopData.children = [nodeId];
          } else if (Array.isArray(loopData.children)) {
            if (!loopData.children.includes(nodeId)) {
              (loopData.children as string[]).push(nodeId);
            }
          }

          // 确保父节点在子节点之前
          ensureParentBeforeChild();
        }
        // 情况 2：从有父节点 -> 无父节点（拖出循环容器）
        else if (currentParentId && !newParentId) {
          // 位置已经是绝对位置（因为 ReactFlow 在拖出时会更新）
          // 从旧父节点的 children 中移除
          const oldParent = state.nodes.find((n) => n.id === currentParentId);
          if (oldParent) {
            const loopData = oldParent.data as Record<string, unknown>;
            if (loopData.children && Array.isArray(loopData.children)) {
              loopData.children = (loopData.children as string[]).filter((id) => id !== nodeId);
            }
          }

          delete node.parentId;
          delete node.extent;
          // 保持 newPosition 作为绝对位置
        }
        // 情况 3：从一个循环容器 -> 另一个循环容器
        else if (currentParentId && newParentId && currentParentId !== newParentId) {
          const oldParent = state.nodes.find((n) => n.id === currentParentId);
          if (oldParent) {
            const oldLoopData = oldParent.data as Record<string, unknown>;
            if (oldLoopData.children && Array.isArray(oldLoopData.children)) {
              oldLoopData.children = (oldLoopData.children as string[]).filter((id) => id !== nodeId);
            }
          }

          node.parentId = newParentId;
          node.extent = 'parent';

          // 调整位置为相对于新父节点的位置
          node.position = {
            x: absolutePosition.x - containingLoop!.position.x,
            y: absolutePosition.y - containingLoop!.position.y,
          };

          // 更新新循环容器的 children
          const newLoopData = containingLoop!.data as Record<string, unknown>;
          if (!newLoopData.children) {
            newLoopData.children = [nodeId];
          } else if (Array.isArray(newLoopData.children)) {
            if (!newLoopData.children.includes(nodeId)) {
              (newLoopData.children as string[]).push(nodeId);
            }
          }

          // 确保父节点在子节点之前
          ensureParentBeforeChild();
        }
        // 情况 4：在同一循环容器内移动 - 不做任何处理，ReactFlow 会自动处理

        state.isDirty = true;
      });
    },
  }))
);

// ==================== ExecutionStore ====================

import {
  ExecutionState,
  NodeExecutionStatus,
  WorkflowContext,
  NodeLogMessage,
} from '../types/workflow';

interface ExecutionStateType {
  // 执行状态
  executionState: ExecutionState;
  currentNodeId: string | null;

  // 调试模式
  isDebugMode: boolean;
  isStepMode: boolean;

  // 执行上下文
  context: WorkflowContext;

  // 节点日志
  nodeLogs: Map<string, NodeExecutionLog>;

  // 执行进度
  progress: {
    completed: number;
    total: number;
  };

  // 错误信息
  error: string | null;

  // 操作方法
  setExecutionState: (state: ExecutionState) => void;
  setCurrentNodeId: (nodeId: string | null) => void;
  setDebugMode: (enabled: boolean) => void;
  setStepMode: (enabled: boolean) => void;
  updateContext: (context: Partial<WorkflowContext>) => void;
  addNodeLog: (nodeId: string, log: NodeLogMessage) => void;
  updateNodeExecution: (nodeId: string, status: NodeExecutionStatus, data?: Partial<NodeExecutionLog>) => void;
  setProgress: (completed: number, total: number) => void;
  setError: (error: string | null) => void;
  resetExecution: () => void;

  // 获取方法
  getNodeLog: (nodeId: string) => NodeExecutionLog | undefined;
  getCurrentNodeLog: () => NodeExecutionLog | undefined;
}

const initialContext: WorkflowContext = {
  task_prompt: '',
  workspace: null,
  loop: null,
  info: {},
};

export const useExecutionStore = create<ExecutionStateType>()(
  immer((set, get) => ({
    // 初始状态
    executionState: ExecutionState.IDLE,
    currentNodeId: null,
    isDebugMode: false,
    isStepMode: false,
    context: initialContext,
    nodeLogs: new Map(),
    progress: { completed: 0, total: 0 },
    error: null,

    // 设置执行状态
    setExecutionState: (state: ExecutionState) => {
      set((s) => {
        s.executionState = state;
      });
    },

    // 设置当前执行节点
    setCurrentNodeId: (nodeId: string | null) => {
      set((s) => {
        s.currentNodeId = nodeId;
      });
    },

    // 设置调试模式
    setDebugMode: (enabled: boolean) => {
      set((s) => {
        s.isDebugMode = enabled;
      });
    },

    // 设置单步模式
    setStepMode: (enabled: boolean) => {
      set((s) => {
        s.isStepMode = enabled;
      });
    },

    // 更新上下文
    updateContext: (context: Partial<WorkflowContext>) => {
      set((s) => {
        Object.assign(s.context, context);
      });
    },

    // 添加节点日志
    addNodeLog: (nodeId: string, log: NodeLogMessage) => {
      set((s) => {
        const existing = s.nodeLogs.get(nodeId);
        if (existing) {
          existing.messages.push(log);
        } else {
          s.nodeLogs.set(nodeId, {
            node_id: nodeId,
            node_name: log.node_id, // 需要从节点获取名称
            started_at: log.timestamp,
            status: NodeExecutionStatus.RUNNING,
            messages: [log],
          });
        }
      });
    },

    // 更新节点执行状态
    updateNodeExecution: (
      nodeId: string,
      status: NodeExecutionStatus,
      data?: Partial<NodeExecutionLog>
    ) => {
      set((s) => {
        const existing = s.nodeLogs.get(nodeId);
        if (existing) {
          existing.status = status;
          if (data) {
            Object.assign(existing, data);
          }
          if (status === NodeExecutionStatus.COMPLETED || status === NodeExecutionStatus.FAILED) {
            existing.completed_at = new Date().toISOString();
          }
        } else {
          s.nodeLogs.set(nodeId, {
            node_id: nodeId,
            node_name: data?.node_name || nodeId,
            started_at: data?.started_at || new Date().toISOString(),
            status,
            messages: data?.messages || [],
            ...data,
          });
        }
      });
    },

    // 设置进度
    setProgress: (completed: number, total: number) => {
      set((s) => {
        s.progress = { completed, total };
      });
    },

    // 设置错误
    setError: (error: string | null) => {
      set((s) => {
        s.error = error;
      });
    },

    // 重置执行状态
    resetExecution: () => {
      set((s) => {
        s.executionState = ExecutionState.IDLE;
        s.currentNodeId = null;
        s.isDebugMode = false;
        s.isStepMode = false;
        s.context = initialContext;
        s.nodeLogs = new Map();
        s.progress = { completed: 0, total: 0 };
        s.error = null;
      });
    },

    // 获取节点日志
    getNodeLog: (nodeId: string) => {
      return get().nodeLogs.get(nodeId);
    },

    // 获取当前节点日志
    getCurrentNodeLog: () => {
      const { currentNodeId, nodeLogs } = get();
      if (!currentNodeId) return undefined;
      return nodeLogs.get(currentNodeId);
    },
  }))
);

export default useWorkflowStore;
